// slices/products/reducer.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchProductMenu,
  fetchProducts,
  fetchSubProductsLevel,
} from './thunk';
import { MenuItem, ProductListItem, PaginatedProducts } from './types';

// helper type for the fulfilled action of fetchSubProductsLevel
type SubLevelFulfilled = ReturnType<typeof fetchSubProductsLevel.fulfilled>;

interface AsyncState<T> {
  data: T;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

interface ProductsState {
  menu: AsyncState<MenuItem[]>;
  list: AsyncState<ProductListItem[]>;
  pagination: { count: number; next: string | null; previous: string | null };
  subLevels: Record<1 | 2 | 3 | 4 | 5, AsyncState<ProductListItem[]>>;
}

const initialState: ProductsState = {
  menu: { data: [], status: 'idle', error: null },
  list: { data: [], status: 'idle', error: null },
  pagination: { count: 0, next: null, previous: null },
  subLevels: {
    1: { data: [], status: 'idle', error: null },
    2: { data: [], status: 'idle', error: null },
    3: { data: [], status: 'idle', error: null },
    4: { data: [], status: 'idle', error: null },
    5: { data: [], status: 'idle', error: null },
  },
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // — MENU —
    builder.addCase(fetchProductMenu.pending, (state) => {
      state.menu.status = 'loading';
      state.menu.error = null;
    });
    builder.addCase(
      fetchProductMenu.fulfilled,
      (state, action: PayloadAction<MenuItem[]>) => {
        state.menu.status = 'succeeded';
        state.menu.data = action.payload;
      }
    );
    builder.addCase(fetchProductMenu.rejected, (state, action) => {
      state.menu.status = 'failed';
      state.menu.error = action.payload ?? action.error.message ?? null;
    });

    // — LIST —
    builder.addCase(fetchProducts.pending, (state) => {
      state.list.status = 'loading';
      state.list.error = null;
    });
    builder.addCase(
      fetchProducts.fulfilled,
      (state, action: PayloadAction<PaginatedProducts>) => {
        state.list.status = 'succeeded';
        state.list.data = action.payload.results;
        state.pagination = {
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
        };
      }
    );
    builder.addCase(fetchProducts.rejected, (state, action) => {
      state.list.status = 'failed';
      state.list.error = action.payload ?? action.error.message ?? null;
    });

    // — SUB-LEVELS —
    builder.addCase(fetchSubProductsLevel.pending, (state, { meta }) => {
      const lvl = meta.arg.level;
      state.subLevels[lvl].status = 'loading';
      state.subLevels[lvl].error = null;
    });
    builder.addCase(
      fetchSubProductsLevel.fulfilled,
      (state, action: SubLevelFulfilled) => {
        const lvl = action.meta.arg.level;
        state.subLevels[lvl].status = 'succeeded';
        state.subLevels[lvl].data = action.payload;
      }
    );
    builder.addCase(fetchSubProductsLevel.rejected, (state, action) => {
      const lvl = action.meta.arg.level;
      state.subLevels[lvl].status = 'failed';
      state.subLevels[lvl].error =
        action.payload ?? action.error.message ?? null;
    });
  },
});

export default productsSlice.reducer;
